import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Button, MenuItem, TextField } from "@mui/material";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";

import WorkerData from "../../Types/WorkerData";
import { Functions } from "../../Types/Enums/Functions";

export interface EditWorkerDialogResult {
    firstName: string;
    lastName: string;
    function: Functions;
}

interface EditWorkerDialogProps {
    worker: WorkerData;
    isSaving?: boolean;
    onClose: (result: EditWorkerDialogResult | null) => void;
}

const ACCENT = "#c79656";

export default function EditWorkerDialog({ worker, isSaving = false, onClose }: EditWorkerDialogProps) {
    const [mounted, setMounted] = useState(false);
    const [dark, setDark] = useState(false);
    const [firstName, setFirstName] = useState(worker.FirstName);
    const [lastName, setLastName] = useState(worker.LastName);
    const [func, setFunc] = useState<Functions>(worker.Function);

    const firstNameRef = useRef<HTMLInputElement>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);
    const saveRef = useRef<HTMLButtonElement>(null);

    const canSave = firstName.trim().length > 0 && lastName.trim().length > 0 && !isSaving;

    useEffect(() => {
        requestAnimationFrame(() => {
            setMounted(true);
            firstNameRef.current?.focus();
        });
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
        const onColorSchemeChange = (e: MediaQueryListEvent) => setDark(e.matches);
        setDark(!!mq?.matches);
        mq?.addEventListener?.("change", onColorSchemeChange);

        return () => {
            document.body.style.overflow = previousOverflow;
            mq?.removeEventListener?.("change", onColorSchemeChange);
        };
    }, []);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose(null);
                return;
            }

            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSave) {
                e.preventDefault();
                onClose({ firstName: firstName.trim(), lastName: lastName.trim(), function: func });
                return;
            }

            if (e.key === "Tab") {
                const stops = [firstNameRef.current, cancelRef.current, saveRef.current].filter(Boolean) as HTMLElement[];
                const active = document.activeElement as HTMLElement | null;
                const idx = stops.findIndex((el) => el === active);
                if (idx !== -1) {
                    e.preventDefault();
                    const next = e.shiftKey ? (idx - 1 + stops.length) % stops.length : (idx + 1) % stops.length;
                    stops[next].focus();
                }
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [canSave, firstName, func, lastName, onClose]);

    if (typeof document === "undefined") return null;

    const isDark = dark;

    const overlayStyle: React.CSSProperties = {
        position: "fixed",
        inset: 0,
        background: isDark ? "rgba(0,0,0,.6)" : "rgba(0,0,0,.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: mounted ? 1 : 0,
        transition: "opacity 140ms ease-out",
        zIndex: 9999,
    };

    const dialogStyle: React.CSSProperties = {
        width: "92vw",
        maxWidth: 560,
        borderRadius: 18,
        background: isDark ? "#141414" : "#ffffff",
        color: isDark ? "#f5f5f5" : "#111111",
        border: "1px solid rgba(199,150,86,.35)",
        boxShadow: "0 16px 60px rgba(0,0,0,.35)",
        transform: mounted ? "translateY(0) scale(1)" : "translateY(6px) scale(.98)",
        transition: "transform 180ms ease-out",
        padding: 28,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 18,
    };

    const titleStyle: React.CSSProperties = {
        margin: 0,
        fontSize: "1.2rem",
        lineHeight: 1.2,
        display: "flex",
        alignItems: "center",
        gap: 10,
    };

    const actionsStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "flex-end",
        gap: 10,
        marginTop: 4,
    };

    const functionMenuProps = {
        sx: { zIndex: 10001 },
        PaperProps: {
            style: {
                borderRadius: 10,
                border: "1px solid rgba(199,150,86,.25)",
                boxShadow: "0 12px 36px rgba(0,0,0,.24)",
            },
        },
    };

    const dialog = (
        <div style={overlayStyle} onClick={() => !isSaving && onClose(null)} role="presentation">
            <div
                style={dialogStyle}
                role="dialog"
                aria-modal="true"
                aria-labelledby="edit-worker-title"
                onClick={(event) => event.stopPropagation()}
            >
                <h2 id="edit-worker-title" style={titleStyle}>
                    <BadgeRoundedIcon style={{ color: ACCENT }} />
                    Edit worker
                </h2>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <TextField
                        label="First name"
                        size="small"
                        inputRef={firstNameRef}
                        value={firstName}
                        onChange={(event) => setFirstName(event.target.value)}
                        error={firstName.trim().length === 0}
                        fullWidth
                    />
                    <TextField
                        label="Last name"
                        size="small"
                        value={lastName}
                        onChange={(event) => setLastName(event.target.value)}
                        error={lastName.trim().length === 0}
                        fullWidth
                    />
                </div>

                <TextField
                    label="Function"
                    size="small"
                    select
                    value={func}
                    onChange={(event) => setFunc(Number(event.target.value) as Functions)}
                    SelectProps={{ MenuProps: functionMenuProps }}
                    fullWidth
                >
                    <MenuItem value={Functions.Server}>Server</MenuItem>
                    <MenuItem value={Functions.Kitchen}>Kitchen</MenuItem>
                    <MenuItem value={Functions.Dishwashing}>Dishwashing</MenuItem>
                </TextField>

                <div style={actionsStyle}>
                    <Button
                        ref={cancelRef}
                        variant="outlined"
                        startIcon={<CloseRoundedIcon />}
                        onClick={() => onClose(null)}
                        disabled={isSaving}
                        style={{ textTransform: "none", fontWeight: 700, borderRadius: 10, paddingInline: 18, borderColor: "rgba(0,0,0,0.14)", color: isDark ? "#f5f5f5" : "#18181b" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        ref={saveRef}
                        variant="contained"
                        disableElevation
                        startIcon={<SaveRoundedIcon />}
                        onClick={() => canSave && onClose({ firstName: firstName.trim(), lastName: lastName.trim(), function: func })}
                        disabled={!canSave}
                        style={{ textTransform: "none", fontWeight: 700, borderRadius: 10, paddingInline: 18, backgroundColor: canSave ? ACCENT : undefined }}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(dialog, document.body);
}
